import Box from "@mui/material/Box";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import IconButton from "@mui/material/IconButton";
import { IoMdClose } from "react-icons/io";
import { Input } from "../../../Input";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import { useEffect, useRef, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import EventPeriodsInput, {
  EventPeriod,
  parsePeriodsFromBackend,
} from "../../../EventPeriodsInput";
import { useBudget } from "../../../../contexts/BudgetContext/projectbudgets.context";
import AbbottAdditionalInfoSection from "./AbbottAdditionalInfoSection";
import {
  AbbottAdditionalInfo,
  emptyAbbottAdditionalInfo,
} from "../../../../types/budget/abbott-additional-info";

interface DescriptionModalProps {
  isOpen: boolean;
  onClose: () => void;
  info: any;
  onConfirm(body: any): void | Promise<void>;
  /** Exibe bloco "Informações adicionais" (cliente Abbott). */
  showAbbottAdditionalInfo?: boolean;
}

const buildDefaultFormValues = (info: any, showAbbott: boolean) => ({
  tagGMP: "",
  local: "",
  city: "",
  country: "",
  state: "",
  address: "",
  meetingPeriod: "",
  availability: "",
  deadline: "",
  site: "",
  ...(info ?? {}),
  abbottAdditionalInfo: showAbbott
    ? {
        ...emptyAbbottAdditionalInfo(),
        ...(info?.abbottAdditionalInfo ?? {}),
      }
    : undefined,
});

const BudgetDataModal = ({
  onClose,
  isOpen,
  info,
  onConfirm,
  showAbbottAdditionalInfo = false,
}: DescriptionModalProps) => {
  const [eventPeriods, setEventPeriods] = useState<EventPeriod[]>([]);
  const [saving, setSaving] = useState(false);

  const { setIsBudgetDataModalOpen } = useBudget();

  const methods = useForm({
    defaultValues: buildDefaultFormValues(info, showAbbottAdditionalInfo),
    mode: "all",
  });

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = methods;

  const wasOpenRef = useRef(false);
  useEffect(() => {
    if (isOpen && !wasOpenRef.current) {
      reset(buildDefaultFormValues(info, showAbbottAdditionalInfo));
      setEventPeriods(parsePeriodsFromBackend(info?.eventPeriods || []));
    }
    wasOpenRef.current = isOpen;
  }, [isOpen, info, reset, showAbbottAdditionalInfo]);

  useEffect(() => {
    if (!isOpen) return;
    setIsBudgetDataModalOpen(true);
    return () => {
      setIsBudgetDataModalOpen(false);
    };
  }, [isOpen, setIsBudgetDataModalOpen]);

  const handleClose = () => {
    if (saving) return;
    setIsBudgetDataModalOpen(false);
    onClose();
  };

  const onSubmit = async (form: any) => {
    const { rfp, abbottAdditionalInfo, ...rest } = form;
    const formattedPeriods = eventPeriods.map((period) => ({
      inicio: period.inicio ? period.inicio.toISOString().slice(0, 16) : "",
      fim: period.fim ? period.fim.toISOString().slice(0, 16) : "",
    }));

    const payload: Record<string, unknown> = {
      ...rest,
      eventPeriods: formattedPeriods,
    };

    if (showAbbottAdditionalInfo && abbottAdditionalInfo) {
      payload.abbottAdditionalInfo = normalizeAbbottPayload(
        abbottAdditionalInfo as AbbottAdditionalInfo,
      );
    }

    try {
      setSaving(true);
      await Promise.resolve(onConfirm(payload));
      setIsBudgetDataModalOpen(false);
      onClose();
    } finally {
      setSaving(false);
    }
  };

  return (
    <FormProvider {...methods}>
      <Dialog
        onClose={() => {
          if (saving) return;
          handleClose();
        }}
        open={isOpen}
        fullWidth
        maxWidth={"md"}
      >
        <DialogTitle sx={{ m: 0, px: 4, color: "#4B465C" }}>
          Dados do evento
        </DialogTitle>

        <IconButton
          aria-label="close"
          disabled={saving}
          onClick={handleClose}
          sx={{
            position: "absolute",
            right: 16,
            top: 16,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <IoMdClose />
        </IconButton>
        <DialogContent sx={{ px: 4 }}>
          <Box display={"flex"} flexDirection={"column"} gap={2}>
            <Input label="Local" {...register("local")} />
            <Box sx={{ display: "flex", flexDirection: "row", gap: 2 }}>
              <Input label="País" {...register("country")} />
              <Input label="UF" {...register("state")} />
              <Input label="Cidade" {...register("city")} />
            </Box>
            <Input label="Endereço completo" {...register("address")} />
            <Box
              sx={{
                backgroundColor: "rgba(0,0,0,0.2)",
                height: "1px",
                width: "100%",
              }}
              my={1}
            />
            <EventPeriodsInput
              periods={eventPeriods}
              onChange={setEventPeriods}
            />
            <Box
              sx={{
                backgroundColor: "rgba(0,0,0,0.2)",
                height: "1px",
                width: "100%",
              }}
              my={1}
            />
            <Box sx={{ display: "flex", flexDirection: "row", gap: 2 }}>
              <Input
                label="Período de reunião"
                {...register("meetingPeriod")}
              />
              <Input label="Disponibilidade" {...register("availability")} />
              <Input label="Deadline" {...register("deadline")} />
            </Box>
            <Input
              label="Site"
              error={!!errors.site}
              helperText={errors.site?.message as string}
              {...register("site", {
                required: {
                  value: true,
                  message: "O campo Site é obrigatório.",
                },
                validate: (value) => {
                  if (typeof value === "string" && value.trim() === "") {
                    return "O campo Site é obrigatório.";
                  }
                  return true;
                },
              })}
            />

            {showAbbottAdditionalInfo && (
              <>
                <Box
                  sx={{
                    backgroundColor: "rgba(0,0,0,0.2)",
                    height: "1px",
                    width: "100%",
                  }}
                  my={1}
                />
                <AbbottAdditionalInfoSection />
              </>
            )}
          </Box>
        </DialogContent>

        <DialogActions sx={{ px: 4, py: 3 }}>
          <Button
            onClick={handleClose}
            variant="outlined"
            disabled={saving}
          >
            Cancelar
          </Button>
          <Button
            variant="contained"
            disabled={saving}
            onClick={handleSubmit(onSubmit)}
          >
            {saving ? "Salvando..." : "Salvar"}
          </Button>
        </DialogActions>
      </Dialog>
    </FormProvider>
  );
};

function normalizeAbbottPayload(
  raw: AbbottAdditionalInfo,
): AbbottAdditionalInfo {
  return {
    contractId: String(raw.contractId ?? "").trim(),
    cventCode: String(raw.cventCode ?? "").trim(),
    observations: String(raw.observations ?? "").trim(),
    terrestrialCoordinator: String(raw.terrestrialCoordinator ?? "").trim(),
    terrestrialPhone: String(raw.terrestrialPhone ?? "").trim(),
    terrestrialEmail: String(raw.terrestrialEmail ?? "").trim(),
    aerialCoordinator: String(raw.aerialCoordinator ?? "").trim(),
    aerialPhone: String(raw.aerialPhone ?? "").trim(),
    aerialEmail: String(raw.aerialEmail ?? "").trim(),
  };
}

export default BudgetDataModal;
